import { useMemo, useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { eventsForDate, minutesToLabel, strToDate, todayStr } from "@/lib/timeUtils";
import { Icon } from "@/lib/icons";
import { Plus, Sparkles, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { motion } from "framer-motion";

const W = 1120;
const H = 860;
const CX = W / 2;
const CY = H / 2;
const RC = 210;
const RE = 360;
const MIN_Z = 0.4;
const MAX_Z = 2.2;

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

function curve(x1, y1, x2, y2, bend = 0) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len, py = dx / len;
  return `M ${x1} ${y1} Q ${mx + px * bend} ${my + py * bend} ${x2} ${y2}`;
}

export function MindMap({ dateStr, onEditEvent, onCreate }) {
  const { events, categories, getCategory, eventColor } = useApp();
  const containerRef = useRef(null);
  const [t, setT] = useState({ z: 1, x: 0, y: 0 });
  const [grabbing, setGrabbing] = useState(false);
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  const pointers = useRef(new Map());
  const panStart = useRef(null);
  const pinchStart = useRef(null);

  const centerFit = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth, h = el.clientHeight;
    const z = clamp(Math.min(w / W, h / H) * 0.92, MIN_Z, 1);
    setT({ z, x: (w - W * z) / 2, y: (h - H * z) / 2 });
  }, []);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(centerFit);
    window.addEventListener("resize", centerFit);
    return () => { cancelAnimationFrame(id); window.removeEventListener("resize", centerFit); };
  }, [centerFit]);

  const zoomAround = (nz, cx, cy) => {
    setT((prev) => {
      const z = clamp(nz, MIN_Z, MAX_Z);
      return { z, x: cx - (cx - prev.x) * (z / prev.z), y: cy - (cy - prev.y) * (z / prev.z) };
    });
  };

  const onWheel = (e) => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const factor = e.deltaY < 0 ? 1.12 : 0.89;
    zoomAround(tRef.current.z * factor, e.clientX - rect.left, e.clientY - rect.top);
  };

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      panStart.current = { sx: e.clientX, sy: e.clientY, ox: tRef.current.x, oy: tRef.current.y };
      setGrabbing(true);
    } else if (pointers.current.size === 2) {
      panStart.current = null;
      const p = [...pointers.current.values()];
      pinchStart.current = { dist: Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1, z: tRef.current.z, x: tRef.current.x, y: tRef.current.y, mx: (p[0].x + p[1].x) / 2, my: (p[0].y + p[1].y) / 2 };
    }
  };

  useEffect(() => {
    const move = (e) => {
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (pointers.current.size >= 2 && pinchStart.current) {
        const p = [...pointers.current.values()];
        const dist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1;
        const ps = pinchStart.current;
        const z = clamp(ps.z * (dist / ps.dist), MIN_Z, MAX_Z);
        const cx = ps.mx - rect.left, cy = ps.my - rect.top;
        setT({ z, x: cx - (cx - ps.x) * (z / ps.z), y: cy - (cy - ps.y) * (z / ps.z) });
      } else if (panStart.current) {
        const ps = panStart.current;
        setT((prev) => ({ ...prev, x: ps.ox + (e.clientX - ps.sx), y: ps.oy + (e.clientY - ps.sy) }));
      }
    };
    const up = (e) => {
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) pinchStart.current = null;
      if (pointers.current.size === 0) { panStart.current = null; setGrabbing(false); }
      else if (pointers.current.size === 1) { const p = [...pointers.current.values()][0]; panStart.current = { sx: p.x, sy: p.y, ox: tRef.current.x, oy: tRef.current.y }; }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); };
  }, []);

  const groups = useMemo(() => {
    const dayEvents = eventsForDate(events, dateStr);
    const map = new Map();
    for (const ev of dayEvents) {
      const key = ev.category_id || "uncat";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    return Array.from(map.entries()).map(([catId, evs]) => ({
      cat: getCategory(catId) || { id: "uncat", name: "General", color: "var(--accent-primary)", icon: "Tag" },
      events: evs,
    }));
  }, [events, dateStr, categories]);

  const layout = useMemo(() => {
    const n = groups.length;
    const nodes = [];
    const links = [];
    groups.forEach((g, i) => {
      const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / Math.max(n, 1);
      const cx = CX + Math.cos(angle) * RC;
      const cy = CY + Math.sin(angle) * RC;
      links.push({ path: curve(CX, CY, cx, cy, (i % 2 ? 1 : -1) * 26), color: g.cat.color, type: "cat", key: `l-${g.cat.id}` });
      const k = g.events.length;
      const spread = Math.min((2 * Math.PI) / Math.max(n, 1) * 0.72, 0.95);
      g.events.forEach((ev, j) => {
        const ea = angle + (j - (k - 1) / 2) * (k > 1 ? spread / (k - 1) : 0);
        const ex = CX + Math.cos(ea) * RE;
        const ey = CY + Math.sin(ea) * RE;
        links.push({ path: curve(cx, cy, ex, ey, (j % 2 ? 1 : -1) * 16), color: g.cat.color, type: "ev", key: `l-${ev.id}` });
        nodes.push({ type: "event", ev, x: ex, y: ey, color: eventColor(ev), delay: 0.15 + i * 0.05 + j * 0.03 });
      });
      nodes.push({ type: "cat", cat: g.cat, count: k, x: cx, y: cy, delay: 0.08 + i * 0.05 });
    });
    return { nodes, links };
  }, [groups]);

  const d = strToDate(dateStr);
  const dayName = d.toLocaleDateString("en-US", { weekday: "long" });
  const dateLabel = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const totalEvents = groups.reduce((s, g) => s + g.events.length, 0);
  const isToday = dateStr === todayStr();
  const stop = (e) => e.stopPropagation();

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden touch-none no-select"
      style={{ cursor: grabbing ? "grabbing" : "grab" }} onWheel={onWheel} onPointerDown={onPointerDown} data-testid="mindmap-container">

      <div className="absolute top-0 left-0 origin-top-left" style={{ width: W, height: H, transform: `translate(${t.x}px, ${t.y}px) scale(${t.z})` }}>
        <svg width={W} height={H} className="absolute inset-0 pointer-events-none">
          <defs>
            <radialGradient id="mm-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--accent-glow)" stopOpacity="0.5" />
              <stop offset="100%" stopColor="var(--accent-glow)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx={CX} cy={CY} r={220} fill="url(#mm-glow)" />
          {layout.links.map((l) => (
            <motion.path key={l.key} d={l.path} fill="none" stroke={l.color}
              strokeWidth={l.type === "cat" ? 2.5 : 1.5} strokeOpacity={l.type === "cat" ? 0.55 : 0.35} strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.7, delay: l.type === "cat" ? 0.1 : 0.3, ease: "easeInOut" }} />
          ))}
        </svg>

        <motion.button
          initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 220, damping: 18 }}
          onPointerDown={stop} onClick={() => onCreate?.()}
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full flex flex-col items-center justify-center shadow-2xl group"
          style={{ left: CX, top: CY, width: 150, height: 150, background: "var(--accent-gradient)", boxShadow: "0 20px 60px var(--accent-glow)" }}
          data-testid="mindmap-center-node" title="Click to add an event">
          <span className="text-white/80 text-[11px] font-semibold uppercase tracking-widest">{isToday ? "Today" : dateLabel}</span>
          <span className="text-white font-display font-extrabold text-2xl leading-none mt-1">{dayName}</span>
          <span className="text-white/90 text-xs mt-2 flex items-center gap-1"><Sparkles size={12} /> {totalEvents} events</span>
          <span className="absolute -bottom-1 opacity-0 group-hover:opacity-100 transition-opacity text-white/70 text-[10px] flex items-center gap-0.5"><Plus size={10} /> add</span>
        </motion.button>

        {layout.nodes.map((node) => {
          if (node.type === "cat") {
            return (
              <motion.div key={`cat-${node.cat.id}`}
                initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 20, delay: node.delay }}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 px-3.5 py-2 rounded-2xl shadow-lg"
                style={{ left: node.x, top: node.y, background: `color-mix(in srgb, ${node.cat.color} 26%, var(--bg-card))`, border: `1.5px solid ${node.cat.color}` }}
                data-testid="mindmap-category-node">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: node.cat.color }}>
                  <Icon name={node.cat.icon} size={15} className="text-white" />
                </div>
                <span className="font-semibold text-sm whitespace-nowrap" style={{ color: "var(--text-primary)" }}>{node.cat.name}</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md" style={{ background: node.cat.color, color: "#fff" }}>{node.count}</span>
              </motion.div>
            );
          }
          const ev = node.ev;
          return (
            <motion.button key={`ev-${ev.id}`}
              initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 280, damping: 22, delay: node.delay }}
              whileHover={{ scale: 1.06, zIndex: 20 }}
              onPointerDown={stop} onClick={() => onEditEvent(ev)}
              className="absolute -translate-x-1/2 -translate-y-1/2 rounded-xl px-3 py-2 text-left shadow-md min-w-[120px] max-w-[180px]"
              style={{ left: node.x, top: node.y, background: `color-mix(in srgb, ${node.color} 18%, var(--bg-card))`, border: `1px solid color-mix(in srgb, ${node.color} 40%, transparent)`, opacity: ev.completed ? 0.55 : 1 }}
              data-testid="mindmap-event-node">
              <div className="flex items-center gap-1.5 mb-0.5">
                {ev.icon && <Icon name={ev.icon} size={12} style={{ color: node.color }} className="shrink-0" />}
                <span className="font-semibold text-[12px] truncate" style={{ color: "var(--text-primary)", textDecoration: ev.completed ? "line-through" : "none" }}>{ev.title}</span>
              </div>
              <span className="text-[10px] font-mono tnum" style={{ color: "var(--text-secondary)" }}>{minutesToLabel(ev.start_minutes, "12")}</span>
            </motion.button>
          );
        })}
      </div>

      {totalEvents === 0 && (
        <div className="absolute inset-x-0 flex justify-center pointer-events-none" style={{ top: "62%" }}>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>No events yet — tap the center to plant your first one</p>
        </div>
      )}

      {/* Zoom / pan controls */}
      <div className="absolute bottom-4 right-4 flex flex-col items-center gap-1.5 p-1.5 rounded-2xl glass shadow-xl" onPointerDown={stop} data-testid="mindmap-controls">
        <button onClick={() => zoomAround(tRef.current.z * 1.2, containerRef.current.clientWidth / 2, containerRef.current.clientHeight / 2)}
          className="w-9 h-9 rounded-xl flex items-center justify-center hover:scale-110 transition-transform" style={{ background: "var(--bg-card)", color: "var(--text-secondary)" }} data-testid="mindmap-zoom-in" title="Zoom in"><ZoomIn size={17} /></button>
        <span className="text-[10px] font-mono tnum py-0.5" style={{ color: "var(--text-muted)" }}>{Math.round(t.z * 100)}%</span>
        <button onClick={() => zoomAround(tRef.current.z * 0.83, containerRef.current.clientWidth / 2, containerRef.current.clientHeight / 2)}
          className="w-9 h-9 rounded-xl flex items-center justify-center hover:scale-110 transition-transform" style={{ background: "var(--bg-card)", color: "var(--text-secondary)" }} data-testid="mindmap-zoom-out" title="Zoom out"><ZoomOut size={17} /></button>
        <button onClick={centerFit}
          className="w-9 h-9 rounded-xl flex items-center justify-center hover:scale-110 transition-transform mt-0.5" style={{ background: "var(--accent-primary)", color: "#fff" }} data-testid="mindmap-reset-view" title="Fit to screen"><Maximize2 size={16} /></button>
      </div>
    </div>
  );
}
